/**
 * Column formulas of the grids: Excel-like expressions evaluated row by row (see
 * `language.ts` and docs/Grid/FORMULAS.md). There are two kinds, and they are
 * different enough to be stored separately:
 *
 *   - `columns`   — NEW calculated columns that do not exist in the page's code at all. They
 *                  need a full definition: title, format, width.
 *   - `overrides` — a formula assigned to a column that ALREADY exists in the page's code,
 *                  replacing where its value comes from. Only the formula text is needed.
 *
 * Like the header renames, this is configuration SHARED by every user: it lives in the
 * `ajustes` table under the key `formulas_grilla`, which any authenticated user can read and
 * only a privileged one (administrator/programmer) can write — the RLS of `ajustes` already
 * enforces that, so no new permission plumbing is needed.
 *
 * A calculated column's `id` is IMMUTABLE and its effective field is `calc:${id}`, so renaming
 * its title (which goes through `encabezados_grilla`, like any other column) cannot break the
 * formulas that reference it. A formula is stored as STRUCTURE, not text — an AST keyed by
 * document/sheet/column ids (`stored.ts`) — and is rendered into the user's own names and
 * separators only for display. `migrateV2.ts` upgrades the older text form on read.
 */

import {
  DEFAULT_FORMAT,
  EMPTY_FORMULA_CONFIG,
  type FormulaColumn,
  type GridFormulaConfig,
} from './config'
import { legacyVersionOf, upgradeStoredFormula } from './migrateV2'
import { STORED_VERSION, type StoredFormula } from './stored'

export type GridFormulas = Record<string, GridFormulaConfig>

export const defaultGridFormulas: GridFormulas = {}


export type { GridFormulaConfig }
export { EMPTY_FORMULA_CONFIG }

/** A fresh immutable id for a new calculated column. Never reused: a reused id would silently
 *  rewire the references of the deleted column onto the new one. */
export function newFormulaId(): string {
  return Math.random().toString(36).slice(2, 10)
}

const configOf = (all: GridFormulas, gridKey: string): GridFormulaConfig => all[gridKey] ?? EMPTY_FORMULA_CONFIG

/** Drops the grid's entry entirely once it has neither calculated columns nor overrides, so an
 *  emptied grid leaves no trace in `ajustes`. */
function withConfig(all: GridFormulas, gridKey: string, config: GridFormulaConfig): GridFormulas {
  const next = { ...all }
  const hasDisabledCellOverrides = Object.values(config.allowCellOverrides ?? {}).some((allowed) => !allowed)
  const hasOverrideTargets = Object.keys(config.overrideTargets ?? {}).length > 0
  if (config.columns.length === 0 && Object.keys(config.overrides).length === 0 && !hasDisabledCellOverrides && !hasOverrideTargets) delete next[gridKey]
  else next[gridKey] = config
  return next
}

/** Missing policy entries are deliberately permissive for backwards compatibility. */
export function cellOverridesAllowed(config: GridFormulaConfig, field: string): boolean {
  return config.allowCellOverrides?.[field] !== false
}

/** Toggle per-cell exceptions for one formula field. Re-enabling removes the explicit
 * entry so old and new configurations share the same default-true representation. */
export function withCellOverridesAllowed(
  all: GridFormulas,
  gridKey: string,
  field: string,
  allowed: boolean,
): GridFormulas {
  const config = configOf(all, gridKey)
  const allowCellOverrides = { ...config.allowCellOverrides }
  if (allowed) delete allowCellOverrides[field]
  else allowCellOverrides[field] = false
  return withConfig(all, gridKey, { ...config, allowCellOverrides })
}

/**
 * Which field a manual entry in `field` back-solves, or `null` for «same column» — the typed
 * value stays in the cell. See `goalSeek.ts`.
 */
export function overrideTargetOf(config: GridFormulaConfig, field: string): string | null {
  const target = config.overrideTargets?.[field]
  return target === undefined || target === '' ? null : target
}

/** Points one formula field's manual entries at another column, or back at itself with `null`. */
export function withOverrideTarget(
  all: GridFormulas,
  gridKey: string,
  field: string,
  target: string | null,
): GridFormulas {
  const config = configOf(all, gridKey)
  const overrideTargets = { ...config.overrideTargets }
  if (target === null || target === '') delete overrideTargets[field]
  else overrideTargets[field] = target
  return withConfig(all, gridKey, { ...config, overrideTargets })
}

/**
 * Copy of `all` with one calculated column added or replaced; with `column === null` it is
 * removed. Same contract as `conEncabezado` in `gridHeaders.ts`.
 */
export function withFormulaColumn(
  all: GridFormulas,
  gridKey: string,
  id: string,
  column: FormulaColumn | null,
): GridFormulas {
  const config = configOf(all, gridKey)
  const index = config.columns.findIndex((c) => c.id === id)
  let columns: FormulaColumn[]
  if (column === null) columns = config.columns.filter((c) => c.id !== id)
  else if (index === -1) columns = [...config.columns, column]
  else columns = config.columns.map((c) => (c.id === id ? column : c))
  const allowCellOverrides = { ...config.allowCellOverrides }
  const overrideTargets = { ...config.overrideTargets }
  if (column === null) {
    delete allowCellOverrides[`calc:${id}`]
    delete overrideTargets[`calc:${id}`]
  }
  return withConfig(all, gridKey, {
    ...config,
    columns,
    ...(config.allowCellOverrides === undefined ? {} : { allowCellOverrides }),
    ...(config.overrideTargets === undefined ? {} : { overrideTargets }),
  })
}

/** Copy of `all` with the formula of an existing column set, or removed with `formula === null`
 *  (which returns the column to the value its own code produces). */
export function withFormulaOverride(
  all: GridFormulas,
  gridKey: string,
  field: string,
  formula: StoredFormula | null,
): GridFormulas {
  const config = configOf(all, gridKey)
  const overrides = { ...config.overrides }
  const allowCellOverrides = { ...config.allowCellOverrides }
  const overrideTargets = { ...config.overrideTargets }
  if (formula === null) {
    delete overrides[field]
    delete allowCellOverrides[field]
    delete overrideTargets[field]
  }
  else overrides[field] = formula
  return withConfig(all, gridKey, {
    ...config,
    overrides,
    ...(config.allowCellOverrides === undefined ? {} : { allowCellOverrides }),
    ...(config.overrideTargets === undefined ? {} : { overrideTargets }),
  })
}

/** Copy of `all` with a grid's calculated columns reordered to match `ids`. Ids missing from the
 *  list keep their relative order at the end, so a stale list cannot drop a column. */
export function withFormulaOrder(all: GridFormulas, gridKey: string, ids: readonly string[]): GridFormulas {
  const config = all[gridKey]
  if (config === undefined) return all
  const byId = new Map(config.columns.map((c) => [c.id, c]))
  const ordered: FormulaColumn[] = []
  for (const id of ids) {
    const column = byId.get(id)
    if (column !== undefined) {
      ordered.push(column)
      byId.delete(id)
    }
  }
  return { ...all, [gridKey]: { ...config, columns: [...ordered, ...byId.values()] } }
}

/**
 * Discards anything malformed from an unknown `valor` read out of `ajustes`.
 *
 * Unlike the other grid settings, this one CANNOT be cast blindly: a corrupt value — or one
 * written by a future version — would crash the Grid on every page instead of degrading a single
 * visual preference. Anything that does not look like a formula config is dropped silently.
 */
export function normaliseGridFormulas(value: unknown): GridFormulas {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return defaultGridFormulas
  const result: GridFormulas = {}
  for (const [gridKey, raw] of Object.entries(value as Record<string, unknown>)) {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) continue
    // The marker only distinguishes v1 from v2, both of which are strings: under v1 a bare
    // `[field]` meant the current row. Telling a stored string from a v3 object needs no marker
    // at all, which is what keeps the upgrade idempotent even when the marker is wrong.
    const {
      columns: rawColumns,
      overrides: rawOverrides,
      syntaxVersion: rawSyntaxVersion,
      allowCellOverrides: rawAllowCellOverrides,
      overrideTargets: rawOverrideTargets,
    } = raw as Record<string, unknown>
    const legacyVersion = legacyVersionOf(rawSyntaxVersion)

    const columns: FormulaColumn[] = []
    const seen = new Set<string>()
    if (Array.isArray(rawColumns)) {
      for (const item of rawColumns) {
        if (typeof item !== 'object' || item === null) continue
        const { id, title, formula, format, minWidth } = item as Record<string, unknown>
        if (typeof id !== 'string' || id === '' || seen.has(id)) continue
        if (typeof title !== 'string') continue
        // A v2 string is upgraded here, once, on read; the new shape persists on the next save.
        const upgraded = upgradeStoredFormula(formula, legacyVersion)
        if (upgraded === undefined) continue
        seen.add(id)
        columns.push({
          id,
          title,
          formula: upgraded,
          format: typeof format === 'string' && format !== '' ? format : DEFAULT_FORMAT,
          ...(typeof minWidth === 'number' && Number.isFinite(minWidth) ? { minWidth } : {}),
        })
      }
    }

    const overrides: Record<string, StoredFormula> = {}
    if (typeof rawOverrides === 'object' && rawOverrides !== null && !Array.isArray(rawOverrides)) {
      for (const [field, formula] of Object.entries(rawOverrides as Record<string, unknown>)) {
        if (field === '') continue
        const upgraded = upgradeStoredFormula(formula, legacyVersion)
        if (upgraded !== undefined) overrides[field] = upgraded
      }
    }

    const allowCellOverrides: Record<string, boolean> = {}
    if (typeof rawAllowCellOverrides === 'object' && rawAllowCellOverrides !== null && !Array.isArray(rawAllowCellOverrides)) {
      for (const [field, allowed] of Object.entries(rawAllowCellOverrides as Record<string, unknown>)) {
        if (field !== '' && allowed === false) allowCellOverrides[field] = false
      }
    }

    const overrideTargets: Record<string, string> = {}
    if (typeof rawOverrideTargets === 'object' && rawOverrideTargets !== null && !Array.isArray(rawOverrideTargets)) {
      for (const [field, target] of Object.entries(rawOverrideTargets as Record<string, unknown>)) {
        // A target pointing at its own column is «same column» spelled the long way: dropped, so
        // the stored shape has exactly one representation of the default.
        if (field !== '' && typeof target === 'string' && target !== '' && target !== field) {
          overrideTargets[field] = target
        }
      }
    }

    if (columns.length > 0 || Object.keys(overrides).length > 0 || Object.keys(allowCellOverrides).length > 0 || Object.keys(overrideTargets).length > 0) {
      result[gridKey] = {
        columns,
        overrides,
        // Every formula above was upgraded on the way in, so what this map holds IS the current
        // shape whatever the stored marker said — which is why the old marker is not consulted.
        syntaxVersion: STORED_VERSION,
        allowCellOverrides,
        overrideTargets,
      }
    }
  }
  return result
}

/**
 * The whole map is persisted by `GridConfigProvider.save`, which knows the
 * organization. TMS wrote it from here into a single-tenant `ajustes` table;
 * keeping the reducers above pure is what lets them be unit-tested and reused.
 */
